// src/components/AdminPanel/PostReservation.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import LoadingAnimation from "../LoadingAnimation";
import { toast } from "react-toastify";
import JoditEditor from "jodit-react";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

// ─────────────────────────────────────────────
// RICH TEXT EDITOR COMPONENT
// ─────────────────────────────────────────────

const RichTextEditor = ({ value, onChange }) => {
  const editor = useRef(null);

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "Write description here...",
      theme: "default",
      toolbar: true,
      toolbarAdaptive: true,
      toolbarSticky: false,
      buttons: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "ul",
        "ol",
        "indent",
        "outdent",
        "align",
        "font",
        "fontsize",
        "image",
        "link",
        "brush",
        "eraser",
      ],
      showXPathInStatusbar: false,
      showWordsCounter: true,
      showCharsCounter: true,
      spellcheck: true,
      minHeight: 300,
    }),
    []
  );

  return (
    <JoditEditor
      ref={editor}
      value={value}
      config={config}
      tabIndex={1}
      onBlur={(newContent) => onChange(newContent)}
      onChange={() => {}}
    />
  );
};

// ─────────────────────────────────────────────
// DEFAULT OPPORTUNITY TYPES
// ─────────────────────────────────────────────

const DEFAULT_OPPORTUNITY_TYPES = [
  "Three Months Unpaid Internship",
  "6 Months Paid PSEB Internship",
  "Permanent Job"
];

const PostReservation = () => {
  const [reservationData, setReservationData] = useState({
    date: "",
    type: "",
    gender: "",
    description: "",
  });

  const [reservations, setReservations] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletePopup, setDeletePopup] = useState({ visible: false, reservationId: null });
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const formTopRef = useRef(null);
  const deletePopupOverlayRef = useRef(null);
  
  // Description modal state
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [tempDescription, setTempDescription] = useState("");
  
  // Dynamic data management for opportunity types
  const [opportunityTypes, setOpportunityTypes] = useState(DEFAULT_OPPORTUNITY_TYPES);
  
  // Add modals
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  
  // Manage modals
  const [showManageTypesModal, setShowManageTypesModal] = useState(false);
  
  // Inline editing state
  const [editingTypeIndex, setEditingTypeIndex] = useState(null);
  const [editingTypeValue, setEditingTypeValue] = useState("");
  
  // Delete confirmations
  const [deleteTypeConfirm, setDeleteTypeConfirm] = useState(null);
  
  // Modal refs for outside click
  const manageTypesOverlayRef = useRef(null);
  const typeModalOverlayRef = useRef(null);

  // Default location
  const defaultLocation = "B-343, Pagganwala Street, Near Cheema Masjid, Shadman Colony, Gujrat, Pakistan.";

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setReservationData((prev) => ({ ...prev, date: today }));
    fetchReservations();
    loadCustomData();
  }, []);

  const loadCustomData = () => {
    const savedTypes = localStorage.getItem("opportunityTypes");
    if (savedTypes) setOpportunityTypes(JSON.parse(savedTypes));
  };

  const saveCustomData = (types) => {
    localStorage.setItem("opportunityTypes", JSON.stringify(types));
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "reservations"));
      const reservationList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReservations(reservationList);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      toast.error("Error fetching reservations");
    } finally {
      setLoading(false);
    }
  };

  // ── ADD HANDLERS ─────────────────────────────────────

  const handleAddType = () => {
    if (!newTypeName.trim()) { toast.error("Please enter an opportunity type"); return; }
    const typeName = newTypeName.trim();
    if (opportunityTypes.includes(typeName)) { toast.error("Opportunity type already exists!"); return; }
    const updatedTypes = [...opportunityTypes, typeName];
    setOpportunityTypes(updatedTypes);
    saveCustomData(updatedTypes);
    toast.success(`Opportunity type "${typeName}" added successfully!`);
    setNewTypeName("");
    setShowTypeModal(false);
  };

  // ── MANAGE TYPES CRUD ────────────────────────────────

  const handleEditTypeSave = (index) => {
    const trimmed = editingTypeValue.trim();
    if (!trimmed) { toast.error("Opportunity type cannot be empty"); return; }
    const oldName = opportunityTypes[index];
    if (trimmed === oldName) { setEditingTypeIndex(null); return; }
    if (opportunityTypes.includes(trimmed)) { toast.error("Opportunity type already exists!"); return; }
    const updatedTypes = [...opportunityTypes];
    updatedTypes[index] = trimmed;
    setOpportunityTypes(updatedTypes);
    saveCustomData(updatedTypes);
    
    // Also update any reservations that had this type
    const updatePromises = reservations.map(async (reservation) => {
      if (reservation.type === oldName) {
        const docRef = doc(db, "reservations", reservation.id);
        await updateDoc(docRef, { type: trimmed });
      }
    });
    Promise.all(updatePromises).then(() => {
      fetchReservations();
    });
    
    toast.success("Opportunity type updated!");
    setEditingTypeIndex(null);
    setEditingTypeValue("");
  };

  const handleDeleteType = (index) => {
    const name = opportunityTypes[index];
    const updatedTypes = opportunityTypes.filter((_, i) => i !== index);
    setOpportunityTypes(updatedTypes);
    saveCustomData(updatedTypes);
    toast.success(`Opportunity type "${name}" deleted!`);
    setDeleteTypeConfirm(null);
  };

  // ── FORM HANDLERS ─────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReservationData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setReservationData((prev) => ({
      ...prev,
      type: value,
    }));
    setShowTypeDropdown(true);
  };

  const handleTypeSelect = (type) => {
    setReservationData((prev) => ({
      ...prev,
      type: type,
    }));
    setShowTypeDropdown(false);
  };

  const getFilteredTypes = () => {
    if (!reservationData.type) return opportunityTypes;
    return opportunityTypes.filter(type =>
      type.toLowerCase().includes(reservationData.type.toLowerCase())
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !reservationData.type ||
      !reservationData.gender
    ) {
      toast.error("⚠️ Please fill all required fields.");
      return;
    }

    setLoading(true);

    const finalReservationData = {
      type: reservationData.type,
      gender: reservationData.gender,
      date: reservationData.date,
      description: reservationData.description || "",
      location: defaultLocation,
    };

    try {
      if (editingId) {
        const docRef = doc(db, "reservations", editingId);
        await updateDoc(docRef, finalReservationData);
        setEditingId(null);
        toast.success("Opportunity updated successfully!");
      } else {
        await addDoc(collection(db, "reservations"), {
          ...finalReservationData,
          createdAt: serverTimestamp(),
        });
        toast.success("Opportunity posted successfully!");
      }

      fetchReservations();

      const today = new Date().toISOString().split("T")[0];
      setReservationData({
        date: today,
        type: "",
        gender: "",
        description: "",
      });
    } catch (error) {
      console.error("Error adding/updating opportunity:", error);
      toast.error("❌ Error saving opportunity!");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (reservation) => {
    setReservationData(reservation);
    setEditingId(reservation.id);
  
    if (formTopRef.current) {
      formTopRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  
  const handleDelete = (id) => {
    setDeletePopup({ visible: true, reservationId: id });
  };

  const confirmDelete = async () => {
    if (!deletePopup.reservationId) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "reservations", deletePopup.reservationId));
      fetchReservations();
      toast.success("🗑️ Opportunity deleted successfully!");
    } catch (error) {
      console.error("Error deleting opportunity:", error);
      toast.error("❌ Error deleting opportunity!");
    } finally {
      setLoading(false);
      setDeletePopup({ visible: false, reservationId: null });
    }
  };

  const cancelDelete = () => {
    setDeletePopup({ visible: false, reservationId: null });
  };

  const handleOverlayClick = (e, modalRef, closeFunction) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      closeFunction();
    }
  };

  // Modal Styles
  const modalStyles = {
    overlay: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, backdropFilter: "blur(4px)",
    },
    container: {
      background: "white", padding: "32px", borderRadius: "16px",
      width: "420px", maxWidth: "90%", boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    },
    wideContainer: {
      background: "white", padding: "32px", borderRadius: "16px",
      width: "560px", maxWidth: "95%", maxHeight: "80vh", overflowY: "auto",
      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    },
    input: { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", marginBottom: "20px", boxSizing: "border-box" },
    select: { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", marginBottom: "20px", boxSizing: "border-box" },
    buttonPrimary: { background: "#f13737", color: "white", padding: "10px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", marginRight: "12px", fontSize: "13px" },
    buttonSecondary: { backgroundColor: "transparent", color: "#718096", padding: "10px 20px", border: "1px solid #e2e8f0", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
    iconBtn: (color = "#FF8000") => ({ background: "none", border: "none", cursor: "pointer", color, padding: "4px 6px", borderRadius: "4px", display: "inline-flex", alignItems: "center" }),
    listItem: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: "8px", marginBottom: "6px", background: "#f9fafb", border: "1px solid #e2e8f0", gap: "8px" },
    editInput: { flex: 1, padding: "6px 10px", borderRadius: "6px", border: "1px solid #FF8000", fontSize: "14px", outline: "none" },
  };

  const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );

  const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512" fill="currentColor">
      <path d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1L377.9 88L407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9l46.1 46.1l-134.3 134.2c-2.9 2.9-6.5 5-10.4 6.1L186.9 325l16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25c-28.1-28.1-73.7-28.1-101.8 0M88 64c-48.6 0-88 39.4-88 88v272c0 48.6 39.4 88 88 88h272c48.6 0 88-39.4 88-88V312c0-13.3-10.7-24-24-24s-24 10.7-24 24v112c0 22.1-17.9 40-40 40H88c-22.1 0-40-17.9-40-40V152c0-22.1 17.9-40 40-40h112c13.3 0 24-10.7 24-24s-10.7-24-24-24z" />
    </svg>
  );

  const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12l1.41 1.41L13.41 14l2.12 2.12l-1.41 1.41L12 15.41l-2.12 2.12l-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );

  const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );

  const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "pointer" }}>
      <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  const GearIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"></circle>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
    </svg>
  );

  return (
    <div className="postjob-container" ref={formTopRef}>
      {/* Delete Popup */}
      {deletePopup.visible && (
        <div 
          style={modalStyles.overlay}
          ref={deletePopupOverlayRef}
          onClick={(e) => handleOverlayClick(e, deletePopupOverlayRef, cancelDelete)}
        >
          <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "52px", height: "52px", background: "linear-gradient(135deg, #f56565 0%, #ed8936 100%)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <span style={{ color: "white", fontSize: "20px", fontWeight: "bold" }}>!</span>
            </div>
            <h3 style={{ margin: "0 0 12px 0", color: "#2D3748", fontSize: "18px" }}>Confirm Delete</h3>
            <p style={{ margin: "0 0 28px 0", color: "#718096", fontSize: "14px", lineHeight: "1.4" }}>Are you sure you want to delete this opportunity?</p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button style={{ ...modalStyles.buttonSecondary, color: "#000" }} onClick={cancelDelete}>Cancel</button>
              <button style={modalStyles.buttonPrimary} onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Opportunity Type Modal */}
      {showTypeModal && (
        <div 
          style={modalStyles.overlay}
          ref={typeModalOverlayRef}
          onClick={(e) => handleOverlayClick(e, typeModalOverlayRef, () => setShowTypeModal(false))}
        >
          <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: "#2D3748" }}>Add New Opportunity Type</h3>
              <button style={modalStyles.iconBtn("#718096")} onClick={() => setShowTypeModal(false)}><XIcon /></button>
            </div>
            <input type="text" placeholder="Enter opportunity type (e.g., 3 Month Internship)" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} style={modalStyles.input} autoFocus onKeyDown={(e) => e.key === "Enter" && handleAddType()} />
            <div style={{ display: "flex", gap: "12px" }}>
              <button style={modalStyles.buttonPrimary} onClick={handleAddType}>Add Type</button>
              <button style={modalStyles.buttonSecondary} onClick={() => setShowTypeModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* MANAGE OPPORTUNITY TYPES MODAL */}
      {showManageTypesModal && (
        <div 
          style={modalStyles.overlay}
          ref={manageTypesOverlayRef}
          onClick={(e) => handleOverlayClick(e, manageTypesOverlayRef, () => { setShowManageTypesModal(false); setEditingTypeIndex(null); setDeleteTypeConfirm(null); })}
        >
          <div style={modalStyles.wideContainer} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: "#2D3748" }}>Manage Opportunity Types</h3>
              <button style={modalStyles.iconBtn("#718096")} onClick={() => { setShowManageTypesModal(false); setEditingTypeIndex(null); setDeleteTypeConfirm(null); }}><XIcon /></button>
            </div>

            {opportunityTypes.length === 0 && <p style={{ color: "#a0aec0", textAlign: "center", padding: "20px 0" }}>No opportunity types added yet.</p>}

            {opportunityTypes.map((type, index) => (
              <div key={index}>
                {deleteTypeConfirm === index ? (
                  <div style={{ ...modalStyles.listItem, background: "#fff5f5", borderColor: "#feb2b2" }}>
                    <span style={{ flex: 1, fontSize: "14px", color: "#c53030" }}>Delete "<strong>{type}</strong>"?</span>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button style={{ ...modalStyles.iconBtn("#e53e3e") }} title="Confirm Delete" onClick={() => handleDeleteType(index)}><CheckIcon /></button>
                      <button style={{ ...modalStyles.iconBtn("#718096") }} title="Cancel" onClick={() => setDeleteTypeConfirm(null)}><XIcon /></button>
                    </div>
                  </div>
                ) : editingTypeIndex === index ? (
                  <div style={modalStyles.listItem}>
                    <input autoFocus value={editingTypeValue} onChange={(e) => setEditingTypeValue(e.target.value)} style={modalStyles.editInput} onKeyDown={(e) => { if (e.key === "Enter") handleEditTypeSave(index); if (e.key === "Escape") setEditingTypeIndex(null); }} />
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button style={modalStyles.iconBtn("#38a169")} title="Save" onClick={() => handleEditTypeSave(index)}><CheckIcon /></button>
                      <button style={modalStyles.iconBtn("#718096")} title="Cancel" onClick={() => setEditingTypeIndex(null)}><XIcon /></button>
                    </div>
                  </div>
                ) : (
                  <div style={modalStyles.listItem}>
                    <span style={{ flex: 1, fontSize: "14px", fontWeight: "500", color: "#2D3748" }}>{type}</span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button style={modalStyles.iconBtn("#FF8000")} title="Edit" onClick={() => { setEditingTypeIndex(index); setEditingTypeValue(type); }}><EditIcon /></button>
                      <button style={modalStyles.iconBtn("#e53e3e")} title="Delete" onClick={() => setDeleteTypeConfirm(index)}><TrashIcon /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end" }}>
              <button style={modalStyles.buttonPrimary} onClick={() => { setShowManageTypesModal(false); setShowTypeModal(true); }}>+ Add New Type</button>
            </div>
          </div>
        </div>
      )}

      <h2 className="page-heading">
        <i className="fas fa-briefcase" style={{ color: "#FF8000" }}></i>
        {editingId ? "Edit Opportunity" : "Post a New Opportunity"}
      </h2>

      <form onSubmit={handleSubmit} className="postjob-form">
        {/* Opportunity Type with Add and Manage Buttons */}
        <div style={{ position: "relative", width: "100%", marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ flex: 1, position: "relative" }}>
              <input
                type="text"
                name="type"
                placeholder="Enter or select opportunity type"
                value={reservationData.type}
                onChange={handleTypeChange}
                onFocus={() => setShowTypeDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowTypeDropdown(false), 200);
                }}
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "16px",
                }}
              />
              
              {showTypeDropdown && getFilteredTypes().length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    marginTop: "4px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  {getFilteredTypes().map((type) => (
                    <div
                      key={type}
                      onMouseDown={() => handleTypeSelect(type)}
                      style={{
                        padding: "10px 12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => { e.target.style.backgroundColor = "#f7fafc"; }}
                      onMouseLeave={(e) => { e.target.style.backgroundColor = "white"; }}
                    >
                      {type}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="button" onClick={() => setShowTypeModal(true)} title="Add Opportunity Type"
              style={{ width: "fit-content", padding: "12px", backgroundColor: "#fff", border: "1px solid #FF8000", borderRadius: "8px", color: "#FF8000", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <PlusIcon />
            </button>
            <button type="button" onClick={() => setShowManageTypesModal(true)} title="Manage Opportunity Types"
              style={{ width: "fit-content", padding: "12px", backgroundColor: "#fff", border: "1px solid #718096", borderRadius: "8px", color: "#718096", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GearIcon />
            </button>
          </div>
        </div>

        {/* Gender Selection */}
        <select
          name="gender"
          value={reservationData.gender}  
          onChange={handleChange}
          required
          style={{ marginBottom: "16px", width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}
        >
          <option value="">Who Can Apply?</option>
          <option value="Male">Male only</option>
          <option value="Female">Female only</option>
          <option value="Male & Female">Male & Female both</option>
        </select>

        {/* ── DESCRIPTION INPUT WITH POPUP MODAL ── */}
        <div style={{ marginBottom: "16px" }}>
         
          
          {/* Clickable preview box */}
          <div
            onClick={() => {
              setTempDescription(reservationData.description);
              setShowDescriptionModal(true);
            }}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "20px",
              minHeight: "100px",
              cursor: "pointer",
              backgroundColor: "#f9fafb",
              transition: "border-color 0.2s",
              wordBreak: "break-word",
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = "#FF8000"}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = "#e2e8f0"}
          >
            {reservationData.description ? (
              <div dangerouslySetInnerHTML={{ __html: reservationData.description }} />
            ) : (
              <span style={{ color: "#a0aec0" }}>Click here to write description...</span>
            )}
          </div>
          <small style={{ color: "#718096", display: "block", marginTop: "6px" }}>
            Click the box above to open full-screen editor
          </small>
        </div>

        {/* ── FULL SCREEN DESCRIPTION MODAL ── */}
        {showDescriptionModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDescriptionModal(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                width: "90%",
                maxWidth: "1200px",
                height: "85vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#f8fafc",
                  flexShrink: 0,
                }}
              >
                <div>
                  <h3 style={{ margin: 0, color: "#1e293b", fontSize: "1.25rem" }}>
                    Edit Description
                  </h3>
                  <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "0.875rem" }}>
                    Use the toolbar to format your text
                  </p>
                </div>
                <button
                  onClick={() => setShowDescriptionModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e2e8f0"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              {/* Modal Body - Rich Text Editor */}
              <div style={{ flex: 1, overflow: "auto", padding: "20px" }}>
                <RichTextEditor
                  value={tempDescription}
                  onChange={(val) => setTempDescription(val)}
                />
              </div>

              {/* Modal Footer */}
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                  backgroundColor: "#f8fafc",
                  flexShrink: 0,
                }}
              >
                <button
                  onClick={() => setShowDescriptionModal(false)}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "white"}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setReservationData(prev => ({ ...prev, description: tempDescription }));
                    setShowDescriptionModal(false);
                    toast.success("Description saved!");
                  }}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#FF8000",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e67600"}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#FF8000"}
                >
                  Save Description
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Date */}
        <input
          className="date-input"
          type="date"
          name="date"
          value={reservationData.date}
          onChange={handleChange}
          required
          style={{ marginBottom: "16px", width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}
        />

        <button 
          type="submit" 
          disabled={loading}
          style={{
            width : "fit-content",
            backgroundColor: "#FF8000",
            color: "white",
            padding: "12px 24px",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "600",
          }}
        >
          {loading ? "Saving..." : editingId ? "Update Opportunity" : "Post Opportunity"}
        </button>
      </form>

      <div className="job-records">
        <h3>All Posted Opportunities</h3>
        {loading ? (
          <LoadingAnimation />
        ) : reservations.length === 0 ? (
          <p>No opportunities posted yet.</p>
        ) : (
          <div className="table-wrapper">
            <table className="job-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Gender</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((reservation) => (
                  <tr key={reservation.id}>
                    <td>{reservation.type}</td>
                    <td>{reservation.gender}</td>
                    <td>{reservation.date}</td>
                    <td className="action_buttons">
                      <button onClick={() => handleEdit(reservation)}>
                        <EditIcon />
                      </button>
                      <button onClick={() => handleDelete(reservation.id)}>
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostReservation;